import React from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { Button, Modal } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

import { colors } from '../../utils/variables.styles';

import EditableSection from '../../components/EditableSection';
import SelectEdit from '../../components/SelectEdit';
import RangeEdit from '../../components/RangeEdit/RangeEdit';

// Upewnij się, że ścieżka jest poprawna w Twoim projekcie
import selector from './plexiWindowsEditor.selector';

import { infoShape } from '../../types/info.types';
import { availableType } from '../../types/available.types';

import {
  selectFirstHeight,
  selectSecondHeight,
  selectLength,
  selectWidth,
  setSelected as setSelectedParameter,
  setWindows
} from '../Construction/construction.reducer';
import withTranslation from '../../components/WithTranslation/WithTranslation';

const Container = styled.div`
  background: ${colors.whiteer};
  padding: 10px 24px;
  margin-left: -27px;
`;

const Spacer = styled.div`
  padding: 10px 24px;
  font-weight: 600;
  max-width: 300px;
`;

const ImgWrapper = styled.img`
  max-width: 100%;
  display: block;
  margin: 15px auto;
`;

const ButtonWrapper = styled.div`
  padding-top: 5px;
`;

/* =========================
   Helpers (bezpieczne)
   ========================= */
const asArray = (v) => (Array.isArray(v) ? v : []);

const findByIdSafe = (list, id) => {
  const arr = asArray(list);
  const sid = id == null ? '' : String(id);
  return arr.find((el) => String(el?.id) === sid);
};

// Parsuje rozmiar „NxM” z value/width/height
const getSelectedSize = (available, selectedId) => {
  const opt = findByIdSafe(available, selectedId);
  if (!opt) return { width: 0, height: 0 };

  // 1) jeśli obiekt ma width/height liczbowo — bierzemy
  if (typeof opt.width === 'number' && typeof opt.height === 'number') {
    return { width: opt.width, height: opt.height };
  }

  // 2) parsuj z value/label/name
  const raw =
    String(
      opt.value ??
      opt.label ??
      opt.name ??
      ''
    ).toLowerCase();

  // formy „80x60”, „80 x 60”, „80×60”
  const m = raw.match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i);
  if (m) {
    return { width: Number(m[1]) || 0, height: Number(m[2]) || 0 };
  }

  // fallback: pierwsze dwie liczby
  const nums = (raw.match(/\d+(?:\.\d+)?/g) || []).map(Number);
  if (nums.length >= 2) {
    return { width: nums[0] || 0, height: nums[1] || 0 };
  }

  return { width: 0, height: 0 };
};

// Ile sztuk z „available / selected”
const getCountFromAvailable = (available, selectedId) => {
  const opt = findByIdSafe(available, selectedId);
  if (!opt) return 0;

  const raw = String(opt.value ?? opt.label ?? opt.name ?? '').toLowerCase();

  // normalnie weź pierwszą liczbę (np. „2 szt.” -> 2)
  const firstNum = raw.match(/-?\d+/);
  if (firstNum) {
    const n = parseInt(firstNum[0], 10);
    if (!Number.isNaN(n)) return Math.max(0, n);
  }

  // fallbacki
  if (raw.includes('brak')) return 0;
  if (raw.includes('zero')) return 0;
  if (raw.includes('jeden') || raw.includes('1')) return 1;
  if (raw.includes('dwa') || raw.includes('2')) return 2;

  return 0;
};

const ConstructionExtras = ({
                              collapsed,
                              loaded = false,

                              // BACK
                              plexiglass_skylight_back_count_available = [],
                              plexiglass_skylight_back_count_info = null,
                              plexiglass_skylight_back_count_selected = null,
                              plexiglass_skylight_back_size_available = [],
                              plexiglass_skylight_back_size_info = null,
                              plexiglass_skylight_back_size_selected = null,

                              // LEFT
                              plexiglass_skylight_left_count_available = [],
                              plexiglass_skylight_left_count_info = null,
                              plexiglass_skylight_left_count_selected = null,
                              plexiglass_skylight_left_size_available = [],
                              plexiglass_skylight_left_size_info = null,
                              plexiglass_skylight_left_size_selected = null,

                              // RIGHT
                              plexiglass_skylight_right_count_available = [],
                              plexiglass_skylight_right_count_info = null,
                              plexiglass_skylight_right_count_selected = null,
                              plexiglass_skylight_right_size_available = [],
                              plexiglass_skylight_right_size_info = null,
                              plexiglass_skylight_right_size_selected = null,

                              setSelected,
                              t
                            }) => {
  // Modal – analogicznie jak w wersji holes/windows
  const openModal = () => {
    const info =
      plexiglass_skylight_back_count_info ||
      plexiglass_skylight_left_size_info ||
      plexiglass_skylight_right_size_info;

    Modal.info({
      title: info && info.info_title,
      content: (
        <>
          {info ? (
            <>
              {info.img_one ? <ImgWrapper src={info.img_one} /> : null}
              {info.img_two ? <ImgWrapper src={info.img_two} /> : null}
              {info.info_content}
              {info.img_three ? <ImgWrapper src={info.img_three} /> : null}
              {info.img_four ? <ImgWrapper src={info.img_four} /> : null}
            </>
          ) : null}
        </>
      )
    });
  };

  // Wymiary hali
  const width = useSelector(selectWidth);
  const length = useSelector(selectLength);
  const firstHeight = useSelector(selectFirstHeight);
  const secondHeight = useSelector(selectSecondHeight);

  // Redux
  const dispatch = useDispatch();
  const windows = useSelector((state) => state.Construction.windows);

  // Zmiana pozycji jednego świetlika (typ = 'plexi')
  const onPlexiPositionChange = (wall, index, key, value) => {
    const updated = (asArray(windows[wall]?.plexi)).map((d, i) =>
      i === index ? { ...d, [key]: value } : d
    );
    dispatch(setWindows(wall, 'plexi', updated));
  };

  // Zmiana LICZBY świetlików: tworzymy wpisy z aktualnie wybranym rozmiarem dla danej ściany
  const handlePlexiCountChange = (
    wall,
    paramKey,
    selectedId,
    countAvailable,
    sizeAvailable,
    sizeSelectedId
  ) => {
    const plexiCount = getCountFromAvailable(countAvailable, selectedId);
    const { width: itemW, height: itemH } = getSelectedSize(sizeAvailable, sizeSelectedId);

    const current = asArray(windows[wall]?.plexi);
    const updated = Array.from({ length: plexiCount }, (_, index) => {
      const existing = current[index];
      if (existing) {
        return {
          ...existing,
          width: typeof existing.width === 'number' && existing.width > 0 ? existing.width : itemW,
          height: typeof existing.height === 'number' && existing.height > 0 ? existing.height : itemH
        };
      }
      return {
        id: index,
        positionHorizontal: 20,
        positionVertical: 50,
        width: itemW,
        height: itemH
      };
    });

    dispatch(setWindows(wall, 'plexi', updated));
    setSelected(paramKey, selectedId);
  };

  // Zmiana ROZMIARU: aktualizujemy width/height wszystkich świetlików na ścianie
  const handlePlexiSizeChange = (wall, paramKey, selectedId, sizeAvailable) => {
    const { width: itemW, height: itemH } = getSelectedSize(sizeAvailable, selectedId);
    const current = asArray(windows[wall]?.plexi);
    const updated = current.map((d) => ({
      ...d,
      width: itemW,
      height: itemH
    }));

    dispatch(setWindows(wall, 'plexi', updated));
    setSelected(paramKey, selectedId);
  };

  // pomocniczo, aby nie powtarzać w JSX
  const renderWallControls = (
    wallKey,
    wallLabelForCount,
    wallLabelForSize,
    countAvailable,
    countSelected,
    countInfo,
    sizeAvailable,
    sizeSelected,
    sizeInfo,
    maxHorizontalBy
  ) => (
    <>
      <SelectEdit
        selected={countSelected}
        loaded={loaded}
        title={countInfo?.name}
        onChange={(id) =>
          handlePlexiCountChange(
            wallKey,
            wallLabelForCount,
            id,
            countAvailable,
            sizeAvailable,
            sizeSelected
          )
        }
        options={asArray(countAvailable).map((el) => ({ id: el.id, value: `${el.value}` }))}
        nested
      />

      <SelectEdit
        selected={sizeSelected}
        loaded={loaded}
        title={sizeInfo?.name}
        onChange={(id) => handlePlexiSizeChange(wallKey, wallLabelForSize, id, sizeAvailable)}
        options={asArray(sizeAvailable).map((el) => ({ id: el.id, value: `${el.value}` }))}
        nested
      />

      {(asArray(windows[wallKey]?.plexi)).map((item, index, arr) => (
        <React.Fragment key={`${item.id}_group`}>
          <RangeEdit
            key={`${item.id}_horizontal`}
            title={
              arr.length === 1
                ? t('plexi_position_horizontal')
                : t('plexi_position_horizontal') + ' 2'
            }
            min={20}
            step={5}
            max={maxHorizontalBy - 20 - item.width}
            unit={'cm'}
            selected={item.positionHorizontal ?? 20}
            onChange={(value) => onPlexiPositionChange(wallKey, index, 'positionHorizontal', value)}
          />
          <RangeEdit
            key={`${item.id}_vertical`}
            title={
              arr.length === 1
                ? t('plexi_position_vertical')
                : t('plexi_position_vertical') + ' 2'
            }
            min={50}
            step={5}
            max={secondHeight - 13 - item.height}
            unit={'cm'}
            selected={item.positionVertical ?? 50}
            onChange={(value) => onPlexiPositionChange(wallKey, index, 'positionVertical', value)}
          />
        </React.Fragment>
      ))}
    </>
  );

  return (
    <React.Fragment>
      {loaded ? (
        <EditableSection
          collapsed={collapsed}
          title={t('plexiglas_skylights')}
          nested
          subsection={true}
          open={false}
        >
          <Spacer>{t('please_describe_location_of_skylights')}</Spacer>

          {/* BACK WALL (max poziomy wg width) */}
          {renderWallControls(
            'back_wall',
            'plexiglass_skylight_back_count',
            'plexiglass_skylight_back_size',
            plexiglass_skylight_back_count_available,
            plexiglass_skylight_back_count_selected,
            plexiglass_skylight_back_count_info,
            plexiglass_skylight_back_size_available,
            plexiglass_skylight_back_size_selected,
            plexiglass_skylight_back_size_info,
            width
          )}

          {/* LEFT WALL (max poziomy wg length) */}
          {renderWallControls(
            'left_wall',
            'plexiglass_skylight_left_count',
            'plexiglass_skylight_left_size',
            plexiglass_skylight_left_count_available,
            plexiglass_skylight_left_count_selected,
            plexiglass_skylight_left_count_info,
            plexiglass_skylight_left_size_available,
            plexiglass_skylight_left_size_selected,
            plexiglass_skylight_left_size_info,
            length
          )}

          {/* RIGHT WALL (max poziomy wg length) */}
          {renderWallControls(
            'right_wall',
            'plexiglass_skylight_right_count',
            'plexiglass_skylight_right_size',
            plexiglass_skylight_right_count_available,
            plexiglass_skylight_right_count_selected,
            plexiglass_skylight_right_count_info,
            plexiglass_skylight_right_size_available,
            plexiglass_skylight_right_size_selected,
            plexiglass_skylight_right_size_info,
            length
          )}

          <Container>
            <ButtonWrapper>
              <Button icon={<InfoCircleOutlined />} type="primary" block onClick={openModal}>
                {t('more_information')}
              </Button>
            </ButtonWrapper>
          </Container>
        </EditableSection>
      ) : null}
    </React.Fragment>
  );
};

ConstructionExtras.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  setSelected: PropTypes.func.isRequired,
  loaded: PropTypes.bool,

  // BACK
  plexiglass_skylight_back_count_available: PropTypes.arrayOf(availableType),
  plexiglass_skylight_back_count_info: infoShape,
  plexiglass_skylight_back_count_selected: PropTypes.number,
  plexiglass_skylight_back_size_available: PropTypes.arrayOf(availableType),
  plexiglass_skylight_back_size_info: infoShape,
  plexiglass_skylight_back_size_selected: PropTypes.number,

  // LEFT
  plexiglass_skylight_left_count_available: PropTypes.arrayOf(availableType),
  plexiglass_skylight_left_count_info: infoShape,
  plexiglass_skylight_left_count_selected: PropTypes.number,
  plexiglass_skylight_left_size_available: PropTypes.arrayOf(availableType),
  plexiglass_skylight_left_size_info: infoShape,
  plexiglass_skylight_left_size_selected: PropTypes.number,

  // RIGHT
  plexiglass_skylight_right_count_available: PropTypes.arrayOf(availableType),
  plexiglass_skylight_right_count_info: infoShape,
  plexiglass_skylight_right_count_selected: PropTypes.number,
  plexiglass_skylight_right_size_available: PropTypes.arrayOf(availableType),
  plexiglass_skylight_right_size_info: infoShape,
  plexiglass_skylight_right_size_selected: PropTypes.number
};

const mapDispatchToProps = {
  setSelected: setSelectedParameter
};

export default connect(selector, mapDispatchToProps)(withTranslation(ConstructionExtras));
